import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  IUserAccountSettings,
  IReduxRequestNode,
  IStandardGUJRequestParams,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import type { ICheckMail } from "./authorizationSlice";

import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IUserAccountSettingsState extends IUserAccountSettings {
  actions: {
    changePasswordCSRF: TChangePasswordCSRF;
    changeProfilePrivacyCSRF: TChangeProfilePrivacyCSRF;
    setMarketingConsentCSRF: TSetMarketingConsentCSRF;
  };
}

type TChangePasswordCSRF = IReduxRequestNode<
  TChangePasswordRequestCSRF,
  IChangePasswordResponseCSRF
>;
type TChangePasswordRequestCSRF = IChangePasswordParamsCSRF &
  IStandardGUJRequestParams &
  ICheckMail;
type TChangeProfilePrivacyCSRF = IReduxRequestNode<
  TChangeProfilePrivacyRequestCSRF,
  IChangeProfilePrivacyResponseCSRF
>;
type TChangeProfilePrivacyRequestCSRF = IChangeProfilePrivacyParamsCSRF &
  IStandardGUJRequestParams;
type TSetMarketingConsentCSRF = IReduxRequestNode<
  TSetMarketingConsentRequestCSRF,
  ISetMarketingConsentResponseCSRF
>;
type TSetMarketingConsentRequestCSRF = ISetMarketingConsentParamsCSRF &
  IStandardGUJRequestParams;

const initialState: IUserAccountSettingsState = {
  actions: {
    changePasswordCSRF: {},
    changeProfilePrivacyCSRF: {},
    setMarketingConsentCSRF: {},
  },
};

/**
 * Блок смены пароля пользователя
 */
export interface IChangePasswordParamsCSRF {
  // New password
  new_pwd: string;
  // Old password
  pwd: string;
}

export interface IChangePasswordResponseCSRF {
  actions_output: {
    success: string;
    password: string;
  };
  status: string;
}

export const changePasswordCSRF = createAsyncThunk(
  "user/accountSettings/changePasswordCSRF",
  async (params: {
    data: IChangePasswordParamsCSRF;
    onSuccess?: (success: TChangePasswordCSRF) => void;
    onError?: (error: TChangePasswordCSRF) => void;
  }) => {
    const response = new Request.CSRF<
      TChangePasswordRequestCSRF,
      IChangePasswordResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "changePasswordCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "change_password",
          gu_ajax: true,
          checkmail: 1,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

/**
 * Блок изменения приватности в разделе "Обо мне / Открыть информацию"
 */

interface IChangeProfilePrivacyResponseCSRF {
  status: "ok" | "error";
}

interface IChangeProfilePrivacyParamsCSRF {
  profile_privacy: "public" | "closed";
}

export const changeProfilePrivacyCSRF = createAsyncThunk(
  "user/accountSettings/changeProfilePrivacyCSRF",
  async (params: {
    data: IChangeProfilePrivacyParamsCSRF;
    onSuccess?: (success: TChangeProfilePrivacyCSRF) => void;
    onError?: (error: TChangeProfilePrivacyCSRF) => void;
  }) => {
    const response = new Request.CSRF<
      TChangeProfilePrivacyRequestCSRF,
      IChangeProfilePrivacyResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "update_my_data_ajax",
      },
      request: {
        method: "POST",
        data: {
          action: "update_my_data_ajax",
          gu_ajax: true,
          profile_privacy: params.data.profile_privacy,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

/**
 * Блок изменения разрешения на спам
 */

interface ISetMarketingConsentResponseCSRF {
  status: "ok" | "error";
}

interface ISetMarketingConsentParamsCSRF {
  marketing_consent: 0 | 1;
}

export const setMarketingConsentCSRF = createAsyncThunk(
  "user/accountSettings/setMarketingConsent",
  async (params: {
    data: ISetMarketingConsentParamsCSRF;
    onSuccess?: (success: TSetMarketingConsentCSRF) => void;
    onError?: (error: TSetMarketingConsentCSRF) => void;
  }) => {
    const response = new Request.CSRF<
      TSetMarketingConsentRequestCSRF,
      ISetMarketingConsentResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "set_marketing_consent",
      },
      request: {
        method: "POST",
        data: {
          action: "set_marketing_consent",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const accountSettingsSlice = createSlice({
  name: "user/accountSettings",
  initialState,
  reducers: {
    setUserAccountSettings: (
      state: IUserAccountSettingsState,
      action: PayloadAction<IUserAccountSettings>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.personalCabinet.paymentsHistory");
    /**
     * Обработка смены пароля changePasswordCSRF
     */
    // Загрузка
    builder.addCase(
      changePasswordCSRF.pending,
      (state: IUserAccountSettingsState) => {
        state.actions.changePasswordCSRF.isLoading = true;
        state.actions.changePasswordCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      changePasswordCSRF.fulfilled,
      (
        state: IUserAccountSettingsState,
        action: PayloadAction<TChangePasswordCSRF>
      ) => {
        state.actions.changePasswordCSRF = action.payload;
        state.actions.changePasswordCSRF.isLoading = false;
      }
    );

    /**
     * Обработка смены приватности профиля changeProfilePrivacyCSRF
     */
    // Загрузка
    builder.addCase(
      changeProfilePrivacyCSRF.pending,
      (state: IUserAccountSettingsState) => {
        state.actions.changeProfilePrivacyCSRF.isLoading = true;
        state.actions.changeProfilePrivacyCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      changeProfilePrivacyCSRF.fulfilled,
      (
        state: IUserAccountSettingsState,
        action: PayloadAction<TChangeProfilePrivacyCSRF>
      ) => {
        state.actions.changeProfilePrivacyCSRF = action.payload;
        state.actions.changeProfilePrivacyCSRF.isLoading = false;
      }
    );

    /**
     * Обработка разрешения на получение спама
     */
    // Загрузка
    builder.addCase(
      setMarketingConsentCSRF.pending,
      (state: IUserAccountSettingsState) => {
        state.actions.setMarketingConsentCSRF.isLoading = true;
        state.actions.setMarketingConsentCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      setMarketingConsentCSRF.fulfilled,
      (
        state: IUserAccountSettingsState,
        action: PayloadAction<TSetMarketingConsentCSRF>
      ) => {
        state.actions.setMarketingConsentCSRF = action.payload;
        state.actions.setMarketingConsentCSRF.isLoading = false;
      }
    );
  },
});

export const { setUserAccountSettings } = accountSettingsSlice.actions;
export default accountSettingsSlice.reducer;
